import React, { useRef } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    CheckCircle2,
    FileText,
    Hammer,
    Image as ImageIcon,
    ScanLine,
    Upload,
    Video,
} from "lucide-react";
import { evrakTuruLabel } from "@/lib/evrakTuruDetect";
import { Button } from "@/components/ui/button";

const ZONE_ICONS = {
    tutanak: FileText,
    eksper: Hammer,
    foto: ImageIcon,
    video: Video,
    diger: FileText,
};

export function fmtTry(n) {
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
    }).format(n || 0);
}

export function KusurKpi({ label, value, hint, accent = "gold", compact = false }) {
    const ring =
        accent === "red"
            ? "border-rose-500/30 bg-rose-500/5"
            : accent === "emerald"
              ? "border-emerald-500/30 bg-emerald-500/5"
              : accent === "sky"
                ? "border-sky-500/30 bg-sky-500/5"
                : "border-[#a31d21]/25 bg-[#121A2F]/80";
    return (
        <div className={`rounded-lg border ${compact ? "p-2.5" : "p-3"} ${ring}`}>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`font-display text-white mt-0.5 ${compact ? "text-lg" : "text-xl"}`}>
                {value ?? "—"}
            </div>
            {hint ? <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div> : null}
        </div>
    );
}

export function KusurUploadZone({ zone, files, onAdd, onRemove, compact }) {
    const inputRef = useRef(null);
    const Icon = ZONE_ICONS[zone.key] || FileText;
    return (
        <div
            className={`rounded-lg border border-dashed border-white/12 bg-[#0B1220]/50 ${compact ? "p-2.5" : "p-3"}`}
        >
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                    <Icon className="h-3.5 w-3.5 text-[#a31d21] shrink-0" />
                    <span className="text-xs font-medium text-white truncate">{zone.label}</span>
                </div>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 border-white/10 text-[10px]"
                    onClick={() => inputRef.current?.click()}
                >
                    <Upload className="h-3 w-3 mr-0.5" />
                    Ekle
                </Button>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    className="hidden"
                    accept={
                        zone.key === "video"
                            ? "video/*"
                            : zone.key === "tutanak" || zone.key === "eksper"
                              ? ".pdf,image/*"
                              : "image/*,.pdf,.doc,.docx"
                    }
                    onChange={(e) => {
                        onAdd(zone.key, e.target.files);
                        e.target.value = "";
                    }}
                />
            </div>
            {!compact ? <p className="text-[10px] text-slate-600 mb-1.5">{zone.hint}</p> : null}
            {files?.length ? (
                <ul className="space-y-0.5 max-h-20 overflow-y-auto">
                    {files.map((f, i) => (
                        <li
                            key={`${f.name}-${i}`}
                            className="flex items-center justify-between text-[10px] text-slate-400 bg-white/5 rounded px-1.5 py-0.5"
                        >
                            <span className="truncate">{f.name}</span>
                            <button
                                type="button"
                                className="text-rose-400 hover:text-rose-300 ml-1 shrink-0"
                                onClick={() => onRemove(zone.key, i)}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-[10px] text-slate-600">Dosya yok</p>
            )}
        </div>
    );
}

export function KusurSistemEvrakPanel({ evraklar, gruplu, selectedIds, onToggle, onSelectAll, loading }) {
    if (loading) {
        return (
            <div className="flex items-center gap-2 text-[10px] text-slate-500 py-2">
                <span className="animate-pulse">Evraklar yükleniyor…</span>
            </div>
        );
    }
    if (!evraklar?.length) {
        return (
            <div className="rounded border border-dashed border-white/10 p-2 text-[10px] text-slate-500">
                Evrak yok — yükleyin veya dosyadan ekleyin.
            </div>
        );
    }

    const groups = [
        { key: "tutanak", label: "Tutanak" },
        { key: "eksper", label: "Eksper" },
        { key: "foto", label: "Foto" },
        { key: "diger", label: "Diğer" },
    ];

    return (
        <div className="space-y-2">
            <div className="flex justify-between items-center">
                <span className="text-[10px] text-slate-400">{selectedIds.length} seçili</span>
                <button type="button" className="text-[10px] text-[#a31d21] hover:underline" onClick={onSelectAll}>
                    Önerilenleri seç
                </button>
            </div>
            {groups.map((g) => {
                const list = gruplu?.[g.key] || [];
                if (!list.length) return null;
                return (
                    <div key={g.key} className="rounded border border-white/10 bg-black/20 p-2">
                        <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">{g.label}</div>
                        <ul className="space-y-1 max-h-28 overflow-y-auto">
                            {list.map((e) => (
                                <li key={e.id}>
                                    <label className="flex items-start gap-1.5 text-[10px] text-slate-300 cursor-pointer hover:bg-white/5 rounded p-1">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(e.id)}
                                            onChange={() => onToggle(e.id)}
                                            className="mt-0.5"
                                        />
                                        <span className="min-w-0 flex-1">
                                            <span className="text-white block truncate">{e.dosya_adi}</span>
                                            <span className="text-slate-500">{evrakTuruLabel(e.evrak_turu)}</span>
                                        </span>
                                    </label>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
}

export function KusurTutanakPanel({ ts, compact = false }) {
    if (!ts) return null;
    const aa = ts.arac_a || {};
    const ab = ts.arac_b || {};
    const box = compact
        ? "rounded-lg border border-amber-500/20 bg-amber-500/5 p-3"
        : "rounded-xl border border-amber-500/25 bg-amber-500/5 p-4";

    const BeyanBlock = ({ title, text, accent = "slate" }) =>
        text ? (
            <div
                className={`rounded-lg border border-white/10 bg-[#0B1220]/50 p-2.5 ${accent === "emerald" ? "border-emerald-500/20" : ""}`}
            >
                <div
                    className={`text-[9px] uppercase tracking-wider mb-1 ${accent === "emerald" ? "text-emerald-400" : "text-slate-500"}`}
                >
                    {title}
                </div>
                <p className="text-[11px] text-slate-300 whitespace-pre-wrap leading-relaxed">{text}</p>
            </div>
        ) : null;

    return (
        <div className={box}>
            <h3 className="text-sm font-medium text-amber-100 mb-2">KTT Tutanak — Tam Okuma</h3>
            {ts.kaza_tarihi || ts.kaza_yeri ? (
                <p className="text-[10px] text-slate-500 mb-2">
                    {ts.kaza_tarihi || "—"} {ts.kaza_saati || ""} · {ts.kaza_yeri || "—"}
                    {ts.tespit_no ? ` · ${ts.tespit_no}` : ""}
                </p>
            ) : null}
            <div className="grid sm:grid-cols-2 gap-2 text-[11px] text-slate-300 mb-2">
                <div>
                    <div className="text-slate-500 mb-0.5">
                        Araç A — {aa.plaka || "—"}
                        {aa.surucu_ad_soyad ? ` (${aa.surucu_ad_soyad})` : ""}
                    </div>
                    {(aa.isaretli_ktk_ihlalleri || []).length ? (
                        <ul className="list-disc pl-3 text-rose-300">
                            {aa.isaretli_ktk_ihlalleri.map((x, i) => (
                                <li key={i}>{x}</li>
                            ))}
                        </ul>
                    ) : (
                        <span className="text-emerald-400">İşaretli ihlal yok</span>
                    )}
                </div>
                <div>
                    <div className="text-slate-500 mb-0.5">
                        Araç B — {ab.plaka || "—"}
                        {ab.surucu_ad_soyad ? ` (${ab.surucu_ad_soyad})` : ""}
                    </div>
                    {(ab.isaretli_ktk_ihlalleri || []).length ? (
                        <ul className="list-disc pl-3 text-rose-300">
                            {ab.isaretli_ktk_ihlalleri.map((x, i) => (
                                <li key={i}>{x}</li>
                            ))}
                        </ul>
                    ) : (
                        <span className="text-emerald-400">İşaretli ihlal yok</span>
                    )}
                </div>
            </div>
            <div className="space-y-1.5">
                <BeyanBlock title="Müvekkil Beyanı" text={ts.muvekkil_beyani || aa.surucu_beyani} accent="emerald" />
                <BeyanBlock title="Karşı Taraf Beyanı" text={ts.karsi_taraf_beyani || ab.surucu_beyani} />
                {(ts.taniklar || [])
                    .filter((t) => t?.beyan || t?.ad_soyad)
                    .slice(0, 3)
                    .map((t, i) => (
                        <BeyanBlock key={i} title={`Tanık: ${t.ad_soyad || "?"}`} text={t.beyan} />
                    ))}
                {ts.kroki_ozeti ? <BeyanBlock title="Kroki" text={ts.kroki_ozeti} /> : null}
                {ts.olay_anlatimi_ozet ? <BeyanBlock title="Olay Özeti" text={ts.olay_anlatimi_ozet} /> : null}
            </div>
            {ts.guven_skoru != null ? (
                <p className="text-[10px] text-slate-600 mt-2">Okuma güveni: {ts.guven_skoru}/100</p>
            ) : null}
        </div>
    );
}

export function KusurYorumlamaPanel({ yorum }) {
    if (!yorum) return null;
    const olasilikRenk =
        yorum.degisim_olasiligi === "yuksek"
            ? "text-emerald-400"
            : yorum.degisim_olasiligi === "orta"
              ? "text-amber-400"
              : "text-slate-400";

    return (
        <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-violet-300 mb-0.5">
                        Kusur Değişir mi?
                    </div>
                    <h3 className="text-sm font-medium text-white">{yorum.baslik || "Değerlendirme"}</h3>
                </div>
                <div className="flex gap-1.5 text-[10px]">
                    <span
                        className={`rounded-full px-2 py-0.5 border ${yorum.degisebilir_mi ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-slate-500/40 text-slate-300"}`}
                    >
                        {yorum.degisebilir_mi ? "Değişebilir" : "Sabit"}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 border border-white/10 ${olasilikRenk}`}>
                        {yorum.degisim_olasiligi || "—"}
                    </span>
                </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-2 text-[11px]">
                <div className="rounded-lg bg-[#0B1220]/60 border border-white/10 p-2">
                    <div className="text-slate-500 mb-0.5">Dosya / SBM</div>
                    <div className="text-white font-medium">{yorum.oran_etiket_dosya || "—"}</div>
                </div>
                <div className="rounded-lg bg-[#0B1220]/60 border border-white/10 p-2">
                    <div className="text-slate-500 mb-0.5">MBS Analiz</div>
                    <div className="text-white font-medium">{yorum.oran_etiket_analiz || "—"}</div>
                </div>
                <div className="rounded-lg bg-[#0B1220]/60 border border-white/10 p-2">
                    <div className="text-slate-500 mb-0.5">50/50</div>
                    <div className="text-white font-medium">
                        {yorum.elli_elli_analizi?.aktif ? "İncelendi" : "Hayır"}
                    </div>
                </div>
            </div>
            {yorum.degisirse_neden ? (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-2 text-[11px] text-slate-200">
                    <strong className="text-emerald-400">Değişirse:</strong> {yorum.degisirse_neden}
                </div>
            ) : null}
            {yorum.degismezse_neden ? (
                <div className="rounded-lg border border-slate-500/20 p-2 text-[11px] text-slate-300">
                    <strong className="text-slate-400">Değişmezse:</strong> {yorum.degismezse_neden}
                </div>
            ) : null}
            {(yorum.hedef_senaryolar || []).length ? (
                <div className="grid sm:grid-cols-2 gap-2">
                    {yorum.hedef_senaryolar.map((h, i) => (
                        <div key={i} className="rounded border border-white/10 bg-[#0B1220]/40 p-2 text-[10px]">
                            <div className="font-medium text-white">{h.oran}</div>
                            <div className="text-slate-500">{h.gerekce}</div>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

export function KusurOkumaLogPanel({ okumaLog }) {
    if (!okumaLog) return null;
    return (
        <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-3">
            <div className="flex items-center gap-2 text-sky-300 text-xs mb-1.5">
                <ScanLine className="h-3.5 w-3.5" />
                Hızlı Okuma — {okumaLog.okunan_sayisi}/{okumaLog.toplam_sayisi} · {okumaLog.sure_ms}ms
            </div>
            <ul className="text-[10px] text-slate-400 space-y-0.5 max-h-24 overflow-y-auto">
                {(okumaLog.ekler || []).map((e, i) => (
                    <li key={i} className="flex items-center gap-1.5">
                        {e.okundu ? (
                            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                        ) : (
                            <AlertTriangle className="h-3 w-3 text-amber-400" />
                        )}
                        {e.ad} — {e.karakter || 0} kr
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function KusurListBlock({ title, items }) {
    return (
        <div className="rounded-lg border border-white/10 bg-[#121A2F]/60 p-3">
            <h3 className="text-xs font-medium text-white mb-1.5">{title}</h3>
            {!items?.length ? (
                <p className="text-[10px] text-slate-500">Veri yok.</p>
            ) : (
                <ul className="space-y-1 text-[11px] text-slate-300 max-h-40 overflow-y-auto">
                    {items.slice(0, 12).map((it, i) => (
                        <li key={i} className="border-l-2 border-[#a31d21]/40 pl-2">
                            {typeof it === "string" ? it : Object.values(it).filter(Boolean).join(" · ")}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export function KusurOranBar({ label, oranA, oranB, adA, adB }) {
    const a = Number(oranA) || 0;
    const b = Number(oranB) || 0;
    return (
        <div className="rounded-xl border border-white/10 bg-[#0B1220]/60 p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Kusur Dağılımı</div>
            <div className="flex h-3 rounded-full overflow-hidden bg-black/40 mb-2">
                <div className="bg-emerald-500/80 transition-all" style={{ width: `${a}%` }} title={`${adA} %${a}`} />
                <div className="bg-rose-500/80 transition-all" style={{ width: `${b}%` }} title={`${adB} %${b}`} />
            </div>
            <div className="flex justify-between text-xs">
                <span className="text-emerald-300">
                    {adA || "Taraf A"}: <strong>%{a}</strong>
                </span>
                <span className="text-rose-300">
                    {adB || "Taraf B"}: <strong>%{b}</strong>
                </span>
            </div>
        </div>
    );
}

export function KusurDosyaLink({ dosyaId, dosyaNo }) {
    if (!dosyaId) return null;
    return (
        <Link
            to={`/e/sigorta-dosyalari/${dosyaId}`}
            className="text-[10px] text-[#a31d21] hover:underline inline-flex items-center gap-0.5"
        >
            {dosyaNo || `Dosya #${dosyaId}`}
        </Link>
    );
}
